import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Divider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  useTheme,
} from '@mui/material';
import { Info, Palette, RotateCcw } from 'lucide-react';
import { Controller, Control, UseFormSetValue } from 'react-hook-form';
import { DEFAULT_CHAT_WIDGET_THEME, THEME_PRESETS } from '../../../config/constants';
import { Organisation, ThemeConfig } from '../../../types/organization';

// Helper to find matching preset or return 'custom'
const findMatchingPreset = (config: ThemeConfig): string => {
  const preset = THEME_PRESETS.find(
    p =>
      p.config.primaryColor === config.primaryColor &&
      p.config.primaryColorLight === config.primaryColorLight &&
      p.config.primaryColorDark === config.primaryColorDark &&
      p.config.accentColor === config.accentColor &&
      p.config.backgroundColor === config.backgroundColor &&
      p.config.textColor === config.textColor &&
      p.config.bubbleUserBg === config.bubbleUserBg &&
      p.config.bubbleAgentBg === config.bubbleAgentBg
  );
  return preset?.id || 'custom';
};

// Color Picker Field Component
const ColorPickerField = ({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string | undefined;
  onChange: (value: string) => void;
  label: string;
  placeholder: string;
}) => {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <input
          type="color"
          value={value || placeholder}
          onChange={e => onChange(e.target.value)}
          style={{
            width: 40,
            height: 40,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        />
        <TextField
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          size="small"
          placeholder={placeholder}
          sx={{ flex: 1 }}
        />
      </Box>
    </Box>
  );
};

// Color Display Component for view mode
const ColorDisplay = ({
  color,
  label,
  defaultColor,
}: {
  color: string | undefined;
  label: string;
  defaultColor: string;
}) => {
  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          backgroundColor: color || defaultColor,
          border: '1px solid #e5e7eb',
        }}
      />
      <Box>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="body2">{color || defaultColor}</Typography>
      </Box>
    </Box>
  );
};

// Chat Widget Preview Component - matches Theme.md specification
const ChatWidgetPreview = ({
  themeConfig,
  companyName = 'Company Name',
}: {
  themeConfig: ThemeConfig;
  companyName?: string;
}) => {
  const primaryColor = themeConfig.primaryColor || DEFAULT_CHAT_WIDGET_THEME.primaryColor!;
  const primaryColorDark =
    themeConfig.primaryColorDark || DEFAULT_CHAT_WIDGET_THEME.primaryColorDark!;
  const primaryColorLight =
    themeConfig.primaryColorLight || DEFAULT_CHAT_WIDGET_THEME.primaryColorLight!;
  const accentColor = themeConfig.accentColor || DEFAULT_CHAT_WIDGET_THEME.accentColor!;
  const backgroundColor = themeConfig.backgroundColor || DEFAULT_CHAT_WIDGET_THEME.backgroundColor!;
  const textColor = themeConfig.textColor || DEFAULT_CHAT_WIDGET_THEME.textColor!;
  const textColorLight = themeConfig.textColorLight || DEFAULT_CHAT_WIDGET_THEME.textColorLight!;
  const bubbleUserBg = themeConfig.bubbleUserBg || DEFAULT_CHAT_WIDGET_THEME.bubbleUserBg!;
  const bubbleAgentBg = themeConfig.bubbleAgentBg || DEFAULT_CHAT_WIDGET_THEME.bubbleAgentBg!;
  const borderRadiusStyle = themeConfig.borderRadius || DEFAULT_CHAT_WIDGET_THEME.borderRadius!;

  // Border radius map based on style
  const radiusMap = {
    sharp: { sm: '2px', md: '4px', lg: '6px', xl: '8px', full: '9999px' },
    soft: { sm: '6px', md: '10px', lg: '14px', xl: '18px', full: '9999px' },
    rounded: { sm: '8px', md: '12px', lg: '16px', xl: '20px', full: '9999px' },
  };
  const radius = radiusMap[borderRadiusStyle as keyof typeof radiusMap] || radiusMap.rounded;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 3,
        pb: 13,
        backgroundColor: '#e5e7eb',
        borderRadius: 2,
      }}
    >
      {/* Chat Window */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 380,
          height: 580,
          backgroundColor: backgroundColor,
          borderRadius: radius.xl,
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: "'Inter', -apple-system, sans-serif",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            height: 72,
            px: 2,
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.125rem', fontWeight: 600, color: '#fff' }}>
              {companyName}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, opacity: 0.85, color: '#fff' }}>
              AI chatbot
            </Typography>
          </Box>
        </Box>

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* Welcome Message */}
          <Box sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: '1.125rem',
                fontWeight: 500,
                color: textColorLight,
                mb: 0.5,
              }}
            >
              Hi there! 👋
            </Typography>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              How can I help you today?
            </Typography>
          </Box>

          {/* Prefill Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
            {['Book an appointment', 'Check availability', 'Contact support'].map((text, idx) => (
              <Box
                key={idx}
                sx={{
                  backgroundColor: '#fff',
                  color: primaryColorDark,
                  border: `1.5px solid ${primaryColor}`,
                  padding: '10px 16px',
                  borderRadius: radius.full,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  textAlign: 'center',
                  '&:hover': {
                    backgroundColor: primaryColorLight,
                    color: '#fff',
                  },
                }}
              >
                {text}
              </Box>
            ))}
          </Box>

          {/* Agent Message */}
          <Box sx={{ maxWidth: '85%', alignSelf: 'flex-start' }}>
            <Typography
              sx={{
                fontSize: '0.625rem',
                color: textColorLight,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                mb: 0.5,
                pl: 0.5,
              }}
            >
              {companyName} - 10:30 AM
            </Typography>
            <Box
              sx={{
                backgroundColor: bubbleAgentBg,
                color: textColor,
                padding: '12px 16px',
                borderRadius: radius.lg,
                borderBottomLeftRadius: '4px',
                fontSize: '0.9375rem',
                lineHeight: 1.5,
              }}
            >
              Great! I'd be happy to help you book an appointment. What service are you interested
              in?
            </Box>
          </Box>

          {/* User Message */}
          <Box sx={{ maxWidth: '85%', alignSelf: 'flex-end' }}>
            <Box
              sx={{
                backgroundColor: bubbleUserBg,
                color: '#fff',
                padding: '12px 16px',
                borderRadius: radius.lg,
                borderBottomRightRadius: '4px',
                fontSize: '0.9375rem',
                lineHeight: 1.5,
              }}
            >
              I'd like to book a consultation
            </Box>
          </Box>

          {/* Option Buttons */}
          <Box
            sx={{
              backgroundColor: bubbleAgentBg,
              padding: 2,
              borderRadius: radius.lg,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {['30 min consultation', '60 min consultation'].map((text, idx) => (
              <Box
                key={idx}
                sx={{
                  backgroundColor: accentColor,
                  color: '#fff',
                  padding: '10px 18px',
                  borderRadius: radius.full,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'background 0.15s',
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
              >
                {text}
              </Box>
            ))}
            <Box
              sx={{
                backgroundColor: 'transparent',
                color: '#ef4444',
                border: '1px solid #fca5a5',
                padding: '10px 18px',
                borderRadius: radius.full,
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'background 0.15s',
                '&:hover': {
                  backgroundColor: '#fef2f2',
                },
              }}
            >
              Exit booking
            </Box>
          </Box>

          {/* Typing Indicator */}
          <Box sx={{ display: 'flex', gap: 0.5, p: 2 }}>
            {[0, 1, 2].map(i => (
              <Box
                key={i}
                sx={{
                  width: 8,
                  height: 8,
                  backgroundColor: primaryColor,
                  borderRadius: '50%',
                  animation: 'bounce 1.4s infinite',
                  animationDelay: `${i * 0.16}s`,
                  '@keyframes bounce': {
                    '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.5 },
                    '40%': { transform: 'scale(1)', opacity: 1 },
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                flex: 1,
                padding: '12px 16px',
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: radius.full,
                fontSize: '0.9375rem',
                color: textColorLight,
                backgroundColor: '#f9fafb',
              }}
            >
              Type a message...
            </Box>
            <Box
              sx={{
                width: 44,
                height: 44,
                backgroundColor: primaryColor,
                borderRadius: radius.full,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: primaryColorDark,
                },
              }}
            >
              <span style={{ color: '#fff', fontSize: 18 }}>➤</span>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              color: textColorLight,
              textAlign: 'center',
            }}
          >
            powered by <span style={{ color: primaryColor, fontWeight: 500 }}>cacxi.com</span>
          </Typography>
        </Box>
      </Box>

      {/* Chat Toggle Button (floating) */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          right: 20,
          width: 60,
          height: 60,
          backgroundColor: primaryColor,
          borderRadius: radius.full,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          cursor: 'pointer',
        }}
      >
        {/* Material Symbols "mode_comment" outline — same icon the live widget toggler uses */}
        <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff">
          <path d="M20 4v13.17L18.83 16H4V4h16m0-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2z" />
        </svg>
      </Box>
    </Box>
  );
};

interface ChatThemeSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>;
  watchedTheme: ThemeConfig | undefined;
  isEditing: boolean;
  organization: Organisation;
}

const ChatThemeSection = ({
  control,
  setValue,
  watchedTheme,
  isEditing,
  organization,
}: ChatThemeSectionProps) => {
  const theme = useTheme();
  const currentTheme = organization.themeConfig || DEFAULT_CHAT_WIDGET_THEME;
  const [selectedPreset, setSelectedPreset] = useState<string>(() =>
    findMatchingPreset({ ...DEFAULT_CHAT_WIDGET_THEME, ...organization.themeConfig })
  );

  // Re-sync preset selection when the organization changes
  useEffect(() => {
    setSelectedPreset(
      findMatchingPreset({ ...DEFAULT_CHAT_WIDGET_THEME, ...organization.themeConfig })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id]);

  // Handle preset selection
  const handlePresetChange = (presetId: string) => {
    setSelectedPreset(presetId);
    if (presetId !== 'custom') {
      const preset = THEME_PRESETS.find(p => p.id === presetId);
      if (preset) {
        // Update all form values with preset config
        Object.entries(preset.config).forEach(([key, value]) => {
          setValue(`themeConfig.${key}`, value);
        });
      }
    }
  };

  const handleResetToDefault = () => {
    setValue('themeConfig', { ...DEFAULT_CHAT_WIDGET_THEME });
    setSelectedPreset(findMatchingPreset(DEFAULT_CHAT_WIDGET_THEME));
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Theme Settings
          </Typography>
          <Tooltip title="Customize the chat widget appearance for your organization">
            <Info size={16} style={{ marginLeft: '8px' }} />
          </Tooltip>
        </Box>
        {isEditing && (
          <Button
            onClick={handleResetToDefault}
            variant="outlined"
            size="small"
            startIcon={<RotateCcw size={16} />}
          >
            Reset to Default
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Customize the appearance of your chat widget to match your brand.
      </Typography>

      <Divider sx={{ my: 2 }} />

      {isEditing ? (
        <Grid container spacing={3}>
          {/* Left: theme settings */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={3}>
              {/* Theme Preset Selection */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                  Theme Collection
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  {THEME_PRESETS.map(preset => (
                    <Box
                      key={preset.id}
                      onClick={() => handlePresetChange(preset.id)}
                      sx={{
                        cursor: 'pointer',
                        border:
                          selectedPreset === preset.id
                            ? `2px solid ${theme.palette.primary.main}`
                            : '2px solid transparent',
                        borderRadius: 2,
                        p: 1.5,
                        backgroundColor:
                          selectedPreset === preset.id
                            ? `${theme.palette.primary.main}10`
                            : theme.palette.background.paper,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s',
                        minWidth: 120,
                        '&:hover': {
                          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                          borderColor: theme.palette.primary.light,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: preset.config.primaryColor,
                          }}
                        />
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: preset.config.accentColor,
                          }}
                        />
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: preset.config.bubbleAgentBg,
                            border: '1px solid #e5e7eb',
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {preset.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', lineHeight: 1.2 }}
                      >
                        {preset.description}
                      </Typography>
                    </Box>
                  ))}
                  {/* Custom option */}
                  <Box
                    onClick={() => handlePresetChange('custom')}
                    sx={{
                      cursor: 'pointer',
                      border:
                        selectedPreset === 'custom'
                          ? `2px solid ${theme.palette.primary.main}`
                          : '2px solid transparent',
                      borderRadius: 2,
                      p: 1.5,
                      backgroundColor:
                        selectedPreset === 'custom'
                          ? `${theme.palette.primary.main}10`
                          : theme.palette.background.paper,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s',
                      minWidth: 120,
                      '&:hover': {
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        borderColor: theme.palette.primary.light,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                        }}
                      />
                      <Palette size={16} />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Custom
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', lineHeight: 1.2 }}
                    >
                      Create your own theme
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Show color pickers only when Custom is selected */}
              {selectedPreset === 'custom' && (
                <>
                  {/* Primary Colors Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                      Primary Colors
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.primaryColor"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="Primary Color"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.primaryColor!}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.primaryColorLight"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="Primary Light"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.primaryColorLight!}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.primaryColorDark"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="Primary Dark"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.primaryColorDark!}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.accentColor"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="Accent Color"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.accentColor!}
                        />
                      )}
                    />
                  </Grid>

                  {/* Background & Text Colors Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                      Background & Text Colors
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.backgroundColor"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="Background Color"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.backgroundColor!}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.textColor"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="Text Color"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.textColor!}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.textColorLight"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="Light Text Color"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.textColorLight!}
                        />
                      )}
                    />
                  </Grid>

                  {/* Chat Bubble Colors Section */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                      Chat Bubble Colors
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.bubbleUserBg"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="User Bubble Background"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.bubbleUserBg!}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="themeConfig.bubbleAgentBg"
                      control={control}
                      render={({ field }) => (
                        <ColorPickerField
                          value={field.value}
                          onChange={field.onChange}
                          label="Agent Bubble Background"
                          placeholder={DEFAULT_CHAT_WIDGET_THEME.bubbleAgentBg!}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}

              {/* Border Radius Section - always visible */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                  Border Radius Style
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="themeConfig.borderRadius"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>Border Radius</InputLabel>
                      <Select {...field} label="Border Radius" value={field.value || 'rounded'}>
                        <MenuItem value="rounded">Rounded (Large)</MenuItem>
                        <MenuItem value="soft">Soft (Medium)</MenuItem>
                        <MenuItem value="sharp">Sharp (Small)</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Right: live preview */}
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Live Preview
            </Typography>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <ChatWidgetPreview
                themeConfig={watchedTheme || DEFAULT_CHAT_WIDGET_THEME}
                companyName={organization.companyName}
              />
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={3}>
          {/* Left: theme colors */}
          <Grid item xs={12} md={7}>
            <Grid container spacing={3}>
              {/* Primary Colors Display */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                  Primary Colors
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.primaryColor}
                  label="Primary Color"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.primaryColor!}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.primaryColorLight}
                  label="Primary Light"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.primaryColorLight!}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.primaryColorDark}
                  label="Primary Dark"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.primaryColorDark!}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.accentColor}
                  label="Accent Color"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.accentColor!}
                />
              </Grid>

              {/* Background & Text Colors Display */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                  Background & Text Colors
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.backgroundColor}
                  label="Background"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.backgroundColor!}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.textColor}
                  label="Text Color"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.textColor!}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.textColorLight}
                  label="Light Text"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.textColorLight!}
                />
              </Grid>

              {/* Chat Bubble Colors Display */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                  Chat Bubble Colors
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.bubbleUserBg}
                  label="User Bubble"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.bubbleUserBg!}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <ColorDisplay
                  color={currentTheme.bubbleAgentBg}
                  label="Agent Bubble"
                  defaultColor={DEFAULT_CHAT_WIDGET_THEME.bubbleAgentBg!}
                />
              </Grid>

              {/* Border Radius Display */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
                  Border Radius Style
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Style
                  </Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {currentTheme.borderRadius || DEFAULT_CHAT_WIDGET_THEME.borderRadius}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Right: preview */}
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
              Preview
            </Typography>
            <Box sx={{ position: 'sticky', top: 24 }}>
              <ChatWidgetPreview
                themeConfig={currentTheme}
                companyName={organization.companyName}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ChatThemeSection;
